import { motion } from 'motion/react';
import { ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useBrand } from '../context/BrandContext';
import { OptimizedImage } from './OptimizedImage';

export default function Hero() {
  const { assets, content, mission, resolveMediaUrl } = useBrand();

  const scrollToConfigurator = (e: React.MouseEvent) => {
    e.preventDefault();
    const element = document.getElementById('configurator');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-40 bg-ows-white">
      <Helmet>
        <title>Open Water Syndicate | Expedition SUP & Survival Gear</title>
        <meta name="description" content="Explore the OWS EXPEDITION CX2, the world's first true all-terrain expedition SUP. Built for survival redundancy and long-range overlanding on the water." />
        <meta name="keywords" content="expedition SUP, survival paddleboard, overlanding gear, Baja exploration, V-hull SUP, inflatable expedition board" />
        <link rel="canonical" href="https://ais-pre-simrsubn4sndvaezn43il3-146117473355.us-east1.run.app/" />
        {assets.heroVideo ? (
          <link rel="preload" as="video" href={resolveMediaUrl(assets.heroVideo) || ''} type="video/mp4" fetchPriority="high" />
        ) : assets.heroImage ? (
          <link rel="preload" as="image" href={resolveMediaUrl(assets.heroImage) || ''} fetchPriority="high" />
        ) : null}
        {assets.logo && (
          <link rel="preload" as="image" href={resolveMediaUrl(assets.logo) || ''} fetchPriority="high" />
        )}
      </Helmet>
      {/* Background Media with Serene Overlay */}
      <div className="absolute inset-0 z-0">
        {assets.heroVideo ? (
          <video
            autoPlay
            loop
            muted
            playsInline
            preload="auto"
            className="w-full h-full object-cover opacity-90"
            key={`hero-video-${resolveMediaUrl(assets.heroVideo)}`}
            src={resolveMediaUrl(assets.heroVideo)}
          />
        ) : assets.heroImage ? (
          <OptimizedImage
            src={resolveMediaUrl(assets.heroImage)}
            alt="Hero Background"
            className="w-full h-full object-cover opacity-90"
          />
        ) : (
          <div className="w-full h-full bg-slate-100" />
        )}
        <div className="absolute inset-0 bg-gradient-to-b from-white/80 via-white/40 to-white/90"></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.2 }}
        >
          <div className="overflow-hidden mb-10 flex justify-center">
            <div className="relative">
              <OptimizedImage 
                src={resolveMediaUrl(assets.logo)} 
                alt="OWS Logo" 
                className="h-48 w-48 mb-8 drop-shadow-2xl" 
                imgClassName="object-contain"
                fetchPriority="high"
                loading="eager"
                fallback={<span className="text-ows-dark font-display font-bold text-4xl uppercase tracking-widest text-center">{content?.brandName ? content.brandName.split(" ").map(w => w[0]).join("") : "OWS"}</span>}
              />
            </div>
          </div>
          
          <div className="overflow-hidden mb-8">
            <motion.span 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.5 }}
              className="text-[9px] md:text-xs font-mono font-bold tracking-[0.3em] md:tracking-[0.8em] text-ows-teal uppercase mb-6 block"
            >
              Open Water Syndicate
            </motion.span>
            <motion.h1 
              initial={{ y: 150 }}
              animate={{ y: 0 }}
              transition={{ duration: 1.5, ease: [0.16, 1, 0.3, 1] }}
              className="text-5xl md:text-8xl lg:text-[7.5rem] font-display font-light uppercase tracking-tighter text-ows-dark drop-shadow-xl mb-4 leading-[0.85] whitespace-pre-line"
            >
              {content.heroHeadline || "JOIN THE \nSYNDICATE."}
            </motion.h1>
          </div>

          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.2, delay: 0.8, ease: "easeOut" }}
            className="text-xl md:text-2xl text-ows-dark/80 max-w-4xl mx-auto mb-12 font-light tracking-tight leading-relaxed italic font-serif"
          >
            {content.heroSubtitle || "Expedition paddleboarding for everyday adventurers, endurance athletes, and elite open-water explorers."}
          </motion.p>

          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1.5, delay: 1.2 }}
            className="flex flex-col items-center justify-center gap-6 font-mono mb-16 w-full px-4"
          >
            <div className="flex flex-col sm:flex-row gap-6 w-full sm:w-auto">
              <Link 
                to="/coaching" 
                className="px-12 py-6 rounded-full font-bold text-[10px] bg-ows-teal text-white hover:bg-ows-teal/90 transition-all duration-500 uppercase tracking-[0.3em] shadow-xl w-full sm:w-auto text-center"
              >
                Book Training
              </Link>
              <Link 
                to="/expedition-cx2#configurator" 
                className="px-12 py-6 rounded-full font-bold text-[10px] bg-ows-dark text-white hover:bg-[#2a2a2a] transition-all duration-500 uppercase tracking-[0.3em] shadow-xl w-full sm:w-auto text-center"
              >
                Pre-Order CX2 Board
              </Link>
            </div>
            <Link 
              to="/team-building" 
              className="px-12 py-6 rounded-full font-bold text-[10px] bg-transparent text-ows-dark border-2 border-white hover:bg-white hover:text-ows-dark transition-all duration-500 uppercase tracking-[0.3em] w-full sm:w-auto text-center"
            >
              Corporate Bookings
            </Link>
          </motion.div>

          {/* Mission Progress Teaser
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.5, delay: 1.5 }}
            className="inline-flex items-center gap-6 px-8 py-4 bg-white/30 backdrop-blur-md rounded-2xl border border-ows-teal/10 shadow-xl"
          >
            <div className="flex flex-col items-start">
              <span className="text-[8px] font-bold uppercase tracking-widest text-ows-teal mb-1">NEW! OWS EXPEDITION CX2 ISUP</span>
              <span className="text-[10px] font-bold uppercase tracking-widest text-ows-dark">{mission.current} / {mission.goal} Units Secured</span>
            </div>
            <div className="w-32 h-1.5 bg-ows-dark/5 rounded-full overflow-hidden">
              <motion.div 
                initial={{ width: 0 }}
                animate={{ width: `${mission.goal > 0 ? (mission.current / mission.goal) * 100 : 0}%` }}
                transition={{ duration: 2, delay: 2 }}
                className="h-full bg-ows-orange"
              />
            </div>
            <Link to="/expedition-cx2#configurator" className="text-[8px] font-bold uppercase tracking-widest text-ows-orange hover:text-ows-orange/80 transition-colors">
              Pre-Order <ArrowRight className="h-3 w-3 inline-block ml-1" />
            </Link>
          </motion.div> */}
        </motion.div> 
      </div>
    </section>
  );
}
